// CustomerDetails.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import Icon from "../../Assets/images/svg/Icons/CategorieIcon.png";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import { GetApiMethod, PatchApiMethod } from "../../Utils/ApiService";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
import Block from "../../Assets/images/svg/Icons/ban-svgrepo-com.svg";
import Edit from "../../Assets/images/svg/Icons/edit-3-svgrepo-com.svg";
interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  state?: string;
  city?: string;
  pincode?: string;
  totalBookings?: number;
  totalSales?: number;
  isBlocked?: boolean;
}

interface LocationState {
  customers?: Customer[];
}

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { customers: localCustomers } = (location.state as LocationState) || {};

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // use custom success hook
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();

  const fetchCustomerFromAPI = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await GetApiMethod(`customer/${id}?_=${Date.now()}`);
      console.log(response);
      const customerData = response?.data?.data?.customer;
      if (customerData) {
        setCustomer(customerData);
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      const selectedCustomer = localCustomers?.find((c) => c.id === id);
      if (selectedCustomer) {
        setCustomer(selectedCustomer);
      } else {
        fetchCustomerFromAPI();
      }
    }

  }, [id, localCustomers]);

  const handleBlockClick = () => setConfirmOpen(true);

  const handleConfirmBlock = async () => {
    setConfirmOpen(false);
    if (customer?.id) {

      try {
        const newStatus = !customer.isBlocked;

        const payload = {
          isBlocked: newStatus,
        };

        await PatchApiMethod(`customer/${customer.id}`, payload);

        showSuccess(
          `${customer.name} ${newStatus ? "blocked" : "unblocked"
          } successfully!`
        );


        setTimeout(() => {
          navigate('/customers');
        }, 1000);

      } catch (error) {
        const newStatus = !customer.isBlocked;
        console.error(`Error ${newStatus ? "blocking" : "unblocking"} customer:`, error);
      }
    }
  };

  const handleEditClick = () => {
    if (!customer) return;
    navigate(`/customer/${customer.id}/edit`);
  };

  if (loading) {
    return (
      <AuthorizedLayout>
        <Box p={3} display="flex" justifyContent="center">
          <CircularProgress size={50} />
        </Box>
      </AuthorizedLayout>
    );
  }

  if (!customer) {
    return (
      <AuthorizedLayout>
        <Box p={3}>
          <Typography variant="h6">No customer data found</Typography>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Box>
      </AuthorizedLayout>
    );
  }

  return (
    <AuthorizedLayout>
      <Box p={3}>
        <Card className="card-layout">
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <BoxHeader
              dashboard_name="Customer Management"
              image={Icon}
              button_show={true}
              onAdd={() => navigate("/customers/add")}
              tabs={["All Customers"]}
              activeTab={0}
              onTabChange={() => navigate("/customers")}
            />
            <Box>
              <Button

                className="block"
                sx={{ mr: 2 }}
                onClick={handleBlockClick}
              >
                <img src={Block} alt="blockimage" style={{ marginRight: 4 }} />

                {!customer.isBlocked ? "Block Customer" : "Unblock Customer"}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleEditClick}
              >
                <img src={Edit} alt="editimage" style={{ marginRight: 4 }} />
                Edit Info
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle1" className="form-header" mb={2}>
            Customer Details
          </Typography>

          <Table className="details-table">
            <TableBody>
              <TableRow>
                <TableCell className="table-header"><strong>Customer Name</strong></TableCell>
                <TableCell className="table-cell">{customer.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Email ID</strong></TableCell>
                <TableCell className="table-cell">{customer.email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Phone Number</strong></TableCell>
                <TableCell className="table-cell">{customer.phone}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Address</strong></TableCell>
                <TableCell className="table-cell">{customer.address || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>State</strong></TableCell>
                <TableCell className="table-cell">{customer.state || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>City</strong></TableCell>
                <TableCell className="table-cell">{customer.city || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Pincode</strong></TableCell>
                <TableCell className="table-cell">{customer.pincode || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Total Bookings</strong></TableCell>
                <TableCell className="table-cell">{customer.totalBookings || 11}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-header"><strong>Total Sales</strong></TableCell>
                <TableCell className="table-cell">₹{customer.totalSales?.toLocaleString() || 123}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          <WarningAmberIcon sx={{ fontSize: 60, color: "#f44336", mb: 2 }} />
          <Typography variant="body1" sx={{ fontSize: "16px", mb: 0.5 }}>
            Do you want to {!customer.isBlocked ? "Block" : "Unblock"}
          </Typography>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            {customer.name}
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              className="btn-cancel"
              sx={{ minWidth: 120 }}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="block"
              sx={{ minWidth: 120 }}
              onClick={handleConfirmBlock}
            >
              {!customer.isBlocked ? "Block" : "Unblock"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Dialog with custom hook */}
      <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
    </AuthorizedLayout>
  );
};

export default CustomerDetails;
